import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { MdWifi, MdTune, MdToggleOn, MdToggleOff, MdEdit, MdSave, MdCancel, MdHistory } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

export default function CompanyRemoteAccessPage() {
    const { user } = useAuth();
    const { t, language } = useLanguage();
    const [plcCards, setPlcCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingRefId, setEditingRefId] = useState(null);
    const [editRefValueInput, setEditRefValueInput] = useState('');
    const [openHistoryFor, setOpenHistoryFor] = useState(null); // outputId or null
    const [historyByOutput, setHistoryByOutput] = useState({});
    const [historyLoading, setHistoryLoading] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/plc/company/${user.companyId}/remote-access`);
            setPlcCards(res.data.data || []);
        } catch (error) {
            toast.error(t('remoteAccess.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [user.companyId, t]);

    useEffect(() => {
        if (user?.companyId) {
            fetchData();
        }
    }, [fetchData, user?.companyId]);

    // Polling every 10 seconds
    useEffect(() => {
        if (!user?.companyId) return;
        const interval = setInterval(() => {
            api.get(`/plc/company/${user.companyId}/remote-access`)
                .then(res => setPlcCards(res.data.data || []))
                .catch(() => {});
        }, 10000);
        return () => clearInterval(interval);
    }, [user?.companyId]);

    const canTrigger = user.isOwner || user.permissions?.canTriggerOutputs;

    const handleToggleOutput = async (plcId, outputId, currentState) => {
        if (!canTrigger) return;
        try {
            const res = await api.put(`/plc/${plcId}/outputs/${outputId}`, { state: !currentState });
            // Update local state with the new PLC data, filtering for visible items
            setPlcCards(prev => prev.map(plc => {
                if (plc._id === plcId) {
                    const updated = res.data.data;
                    return {
                        ...updated,
                        outputs: (updated.outputs || []).filter(o => o.companyVisible),
                        referenceValues: (updated.referenceValues || []).filter(r => r.companyVisible)
                    };
                }
                return plc;
            }));
            toast.success(t('plc.outputUpdated'));
        } catch (err) {
            toast.error(t('companyDetail.settingFailed'));
        }
    };

    const handleUpdateRefValue = async (plcId, refId) => {
        if (!editRefValueInput.trim()) {
            toast.error(t('plc.referenceValueVal') + ' zorunludur');
            return;
        }
        try {
            const res = await api.put(`/plc/${plcId}/reference-values/${refId}`, { value: editRefValueInput.trim() });
            setPlcCards(prev => prev.map(plc => {
                if (plc._id === plcId) {
                    const updated = res.data.data;
                    return {
                        ...updated,
                        outputs: (updated.outputs || []).filter(o => o.companyVisible),
                        referenceValues: (updated.referenceValues || []).filter(r => r.companyVisible)
                    };
                }
                return plc;
            }));
            setEditingRefId(null);
            toast.success(t('plc.referenceValueUpdated'));
        } catch (err) {
            toast.error(t('companyDetail.settingFailed'));
        }
    };

    const toggleHistory = async (plcId, outputId) => {
        if (openHistoryFor === outputId) {
            setOpenHistoryFor(null);
            return;
        }
        setOpenHistoryFor(outputId);
        if (!historyByOutput[outputId]) {
            try {
                setHistoryLoading(true);
                const res = await api.get(`/plc/${plcId}/outputs/${outputId}/logs`);
                setHistoryByOutput(prev => ({ ...prev, [outputId]: res.data.data || [] }));
            } catch {
                toast.error(t('remoteAccess.historyLoadFailed'));
            } finally {
                setHistoryLoading(false);
            }
        }
    };

    const dateLocale = language === 'tr' ? 'tr-TR' : 'en-US';

    if (loading) return <div className="loading-page"><div className="spinner" style={{ width: 48, height: 48 }}></div></div>;

    if (plcCards.length === 0) {
        return (
            <div className="page-wrapper">
                <div className="empty-state" style={{ padding: '80px 0', maxWidth: 600, margin: '0 auto' }}>
                    <div className="empty-state-icon" style={{ fontSize: 48 }}>🔒</div>
                    <div className="empty-state-title" style={{ fontSize: 18 }}>{t('remoteAccess.noAccess')}</div>
                    <div className="empty-state-desc">{t('remoteAccess.noAccessDesc')}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="page-header" style={{ marginBottom: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16 }}>
                <div>
                    <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                        <MdWifi style={{ color: 'var(--primary)' }} />
                        {t('remoteAccess.title')}
                    </h1>
                    <p className="page-subtitle" style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>
                        {t('remoteAccess.subtitle')}
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 28, width: '100%' }}>
                {plcCards.map(plc => {
                    const hasOutputs = plc.outputs && plc.outputs.length > 0;
                    const hasRefs = plc.referenceValues && plc.referenceValues.length > 0;

                    if (!hasOutputs && !hasRefs) return null;

                    return (
                        <div key={plc._id} className="card" style={{ padding: 24 }}>
                            {/* PLC Card Header */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: 10,
                                    background: 'linear-gradient(135deg, rgba(79,142,247,0.15), rgba(79,142,247,0.05))',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: '1px solid rgba(79,142,247,0.2)'
                                }}>
                                    <MdWifi style={{ color: 'var(--primary)', fontSize: 20 }} />
                                </div>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{plc.plcName}</h2>
                                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{plc.systemId?.systemName}</span>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: hasOutputs && hasRefs ? '1fr 1fr' : '1fr', gap: 24 }}>
                                {/* Reference Values */}
                                {hasRefs && (
                                    <div>
                                        <h3 style={{ margin: '0 0 14px 0', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <MdTune style={{ color: 'var(--primary)' }} />
                                            {t('remoteAccess.referenceValues')}
                                        </h3>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {plc.referenceValues.map(ref => (
                                                <div key={ref._id} style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    background: 'var(--bg-elevated)', padding: '10px 14px', borderRadius: 10,
                                                    border: '1px solid var(--border)'
                                                }}>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{ref.name}</span>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        {editingRefId === ref._id ? (
                                                            <>
                                                                <input
                                                                    className="form-input form-input-sm"
                                                                    value={editRefValueInput}
                                                                    onChange={e => setEditRefValueInput(e.target.value)}
                                                                    style={{ width: 100, height: 30, fontSize: 13, fontFamily: 'monospace', textAlign: 'right' }}
                                                                    autoFocus
                                                                    onKeyDown={e => { if (e.key === 'Enter') handleUpdateRefValue(plc._id, ref._id); if (e.key === 'Escape') setEditingRefId(null); }}
                                                                />
                                                                <button onClick={() => handleUpdateRefValue(plc._id, ref._id)} className="btn btn-success btn-xs btn-icon" style={{ height: 28, width: 28 }}>
                                                                    <MdSave size={14} />
                                                                </button>
                                                                <button onClick={() => setEditingRefId(null)} className="btn btn-secondary btn-xs btn-icon" style={{ height: 28, width: 28 }}>
                                                                    <MdCancel size={14} />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span style={{
                                                                    fontFamily: 'monospace', fontSize: 14, fontWeight: 700,
                                                                    color: 'var(--primary)', background: 'rgba(79,142,247,0.08)',
                                                                    padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(79,142,247,0.15)'
                                                                }}>{ref.value}</span>
                                                                <button
                                                                    onClick={() => { setEditingRefId(ref._id); setEditRefValueInput(ref.value); }}
                                                                    className="btn btn-secondary btn-xs btn-icon"
                                                                    style={{ height: 28, width: 28 }}
                                                                    title={t('common.edit')}
                                                                >
                                                                    <MdEdit size={14} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Switches (24V Outputs) */}
                                {hasOutputs && (
                                    <div>
                                        <h3 style={{ margin: '0 0 14px 0', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <MdToggleOn style={{ color: 'var(--success)' }} />
                                            {t('remoteAccess.switches')}
                                        </h3>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {plc.outputs.map(out => (
                                                <div key={out._id} style={{
                                                    background: 'var(--bg-elevated)', borderRadius: 10,
                                                    border: '1px solid var(--border)', overflow: 'hidden'
                                                }}>
                                                    <div style={{
                                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                        padding: '10px 14px'
                                                    }}>
                                                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{out.name}</span>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <button
                                                                onClick={() => toggleHistory(plc._id, out._id)}
                                                                title={t('remoteAccess.history')}
                                                                style={{
                                                                    background: 'none', border: 'none', cursor: 'pointer',
                                                                    display: 'flex', alignItems: 'center',
                                                                    color: openHistoryFor === out._id ? 'var(--primary)' : 'var(--text-muted)',
                                                                    padding: 0
                                                                }}
                                                            >
                                                                <MdHistory size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleToggleOutput(plc._id, out._id, out.state)}
                                                                disabled={!canTrigger}
                                                                title={!canTrigger ? t('remoteAccess.noTriggerPermission') : undefined}
                                                                style={{
                                                                    background: 'none', border: 'none',
                                                                    cursor: canTrigger ? 'pointer' : 'not-allowed',
                                                                    opacity: canTrigger ? 1 : 0.6,
                                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                                    color: out.state ? 'var(--success)' : 'var(--text-muted)',
                                                                    padding: 0, fontWeight: 700, fontSize: 13
                                                                }}
                                                            >
                                                                {out.state ? (
                                                                    <>
                                                                        <MdToggleOn size={28} />
                                                                        <span>{t('plc.stateOn')}</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <MdToggleOff size={28} />
                                                                        <span>{t('plc.stateOff')}</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {openHistoryFor === out._id && (
                                                        <div style={{ padding: '0 14px 12px', borderTop: '1px solid var(--border)' }}>
                                                            {historyLoading && !historyByOutput[out._id] ? (
                                                                <div style={{ padding: '10px 0', textAlign: 'center' }}><span className="spinner spinner-sm" /></div>
                                                            ) : (historyByOutput[out._id] || []).length === 0 ? (
                                                                <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '10px 0' }}>{t('remoteAccess.historyEmpty')}</div>
                                                            ) : (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10, maxHeight: 180, overflowY: 'auto' }}>
                                                                    {historyByOutput[out._id].map(log => (
                                                                        <div key={log._id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-secondary)' }}>
                                                                            <span>
                                                                                <strong style={{ color: 'var(--text-primary)' }}>{log.userDisplayName}</strong>
                                                                                {' '}{log.previousState ? t('plc.stateOn') : t('plc.stateOff')} → {log.newState ? t('plc.stateOn') : t('plc.stateOff')}
                                                                            </span>
                                                                            <span style={{ fontFamily: 'monospace', flexShrink: 0, marginLeft: 8 }}>
                                                                                {new Date(log.timestamp).toLocaleString(dateLocale)}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
